import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteProjectComment,
  downloadCommentAttachment,
  fetchProjectComments,
  postProjectComment,
  uploadCommentAttachment,
} from "./plugins/commentsApi";
import CommentAuthorDisplay from "./plugins/CommentAuthorDisplay";
import CommentMentionTextarea from "./plugins/CommentMentionTextarea";
import { mentionDisplayLabel } from "./plugins/commentMentions";
import { buildViewerAuthorProfile } from "./plugins/buildViewerAuthorProfile";
import { fetchUserProfile } from "./plugins/profileApi";
import { fetchMarketplaceShop } from "./plugins/marketplaceApi";
import { isPluginEnabled, PLUGIN_IDS } from "./plugins/registry";
import { readStoredAdminFlag } from "./authAdmin";
import { isImpersonating } from "./plugins/userMatrixStorage";
import "./plugins/commentAuthorDisplay.css";

function nestComments(flat) {
  const map = new Map(flat.map((c) => [c.id, { ...c, replies: [] }]));
  const roots = [];
  for (const c of flat) {
    const node = map.get(c.id);
    if (c.parentId && map.has(c.parentId)) {
      map.get(c.parentId).replies.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function CommentBranch({
  node,
  projectId,
  depth,
  onReply,
  replyOpenId,
  onUploadFile,
  onDownload,
  onDelete,
  canDelete,
  deleteBusyId,
  uploadBusyId,
}) {
  const pad = Math.min(depth, 6) * 12;
  const isReplying = replyOpenId === node.id;
  const deleting = deleteBusyId === node.id;

  return (
    <div className="project-comments-branch" style={{ marginLeft: pad }}>
      <div className="project-comments-card">
        <div className="project-comments-card-head">
          <CommentAuthorDisplay email={node.authorEmail} authorProfile={node.authorProfile} />
          <span className="project-comments-when">
            {node.createdAt ? new Date(node.createdAt).toLocaleString() : ""}
          </span>
        </div>
        <div className="project-comments-body">{node.body}</div>
        {node.mentions && node.mentions.length > 0 ? (
          <div className="project-comments-mentions">
            Mentioned:{" "}
            {node.mentions.map((m) => (
              <span key={typeof m === "object" ? m.email : m} className="project-comments-mention-pill">
                @{mentionDisplayLabel(m)}
              </span>
            ))}
          </div>
        ) : null}
        {node.attachments && node.attachments.length > 0 ? (
          <ul className="project-comments-attachments">
            {node.attachments.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  className="project-comments-attach-link"
                  onClick={() => onDownload(projectId, node.id, a.id, a.originalFilename)}
                >
                  {a.originalFilename}
                </button>
                <span className="project-comments-attach-meta">
                  {" "}
                  ({a.byteSize != null ? `${Math.round(a.byteSize / 1024)} KB` : "—"})
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="project-comments-card-actions">
          <button type="button" className="project-comments-reply-btn" onClick={() => onReply(node.id)}>
            Reply
          </button>
          <label className="project-comments-file-label">
            <input
              type="file"
              disabled={uploadBusyId === node.id}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) onUploadFile(node.id, f);
              }}
            />
            <span>{uploadBusyId === node.id ? "Uploading…" : "Attach file"}</span>
          </label>
          {canDelete ? (
            <button
              type="button"
              className="project-comments-delete-btn"
              disabled={deleting}
              onClick={() => onDelete(node)}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          ) : null}
        </div>
      </div>
      {isReplying ? (
        <p className="project-comments-reply-hint">Use the composer below — replying to this thread.</p>
      ) : null}
      {node.replies.map((ch) => (
        <CommentBranch
          key={ch.id}
          node={ch}
          projectId={projectId}
          depth={depth + 1}
          onReply={onReply}
          replyOpenId={replyOpenId}
          onUploadFile={onUploadFile}
          onDownload={onDownload}
          onDelete={onDelete}
          canDelete={canDelete}
          deleteBusyId={deleteBusyId}
          uploadBusyId={uploadBusyId}
        />
      ))}
    </div>
  );
}

export default function ProjectCommentsPanel({
  projectId,
  projectTitle,
  sectionClassName,
  submitButtonClassName = "user-profile-settings-primary",
}) {
  const [flat, setFlat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [composerBody, setComposerBody] = useState("");
  const [replyParentId, setReplyParentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadBusyId, setUploadBusyId] = useState(null);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [viewerProfile, setViewerProfile] = useState(null);
  const [shopCatalog, setShopCatalog] = useState([]);

  const tree = useMemo(() => nestComments(flat), [flat]);
  const viewerAuthorProfile = useMemo(
    () => buildViewerAuthorProfile(viewerProfile, shopCatalog),
    [viewerProfile, shopCatalog]
  );
  const canDeleteComments = readStoredAdminFlag() && !isImpersonating();

  const loadViewerIdentity = useCallback(async () => {
    try {
      const profile = await fetchUserProfile();
      setViewerProfile(profile);
      if (isPluginEnabled(PLUGIN_IDS.MARKETPLACE)) {
        try {
          const shop = await fetchMarketplaceShop();
          setShopCatalog(Array.isArray(shop.catalog) ? shop.catalog : []);
        } catch {
          setShopCatalog([]);
        }
      } else {
        setShopCatalog([]);
      }
    } catch {
      setViewerProfile(null);
      setShopCatalog([]);
    }
  }, []);

  useEffect(() => {
    loadViewerIdentity();
    window.addEventListener("pb-impersonation-changed", loadViewerIdentity);
    return () => window.removeEventListener("pb-impersonation-changed", loadViewerIdentity);
  }, [loadViewerIdentity]);

  const reload = useCallback(() => {
    setError("");
    setLoading(true);
    fetchProjectComments(projectId)
      .then((rows) => setFlat(rows))
      .catch((err) => {
        setError(err?.response?.data?.error || err?.message || "Could not load comments.");
        setFlat([]);
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const submitComment = async () => {
    const body = composerBody.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const created = await postProjectComment(projectId, { body, parentId: replyParentId });
      setFlat((prev) => [...prev, created]);
      setComposerBody("");
      setReplyParentId(null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not post comment.");
    } finally {
      setSubmitting(false);
    }
  };

  const onUploadFile = async (commentId, file) => {
    setUploadBusyId(commentId);
    setError("");
    try {
      const att = await uploadCommentAttachment(projectId, commentId, file);
      setFlat((prev) =>
        prev.map((c) => {
          if (c.id !== commentId) return c;
          const attachments = [...(c.attachments || []), att];
          return { ...c, attachments };
        })
      );
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Upload failed.");
    } finally {
      setUploadBusyId(null);
    }
  };

  const onDownload = async (pid, cid, aid, name) => {
    try {
      await downloadCommentAttachment(pid, cid, aid, name || "attachment");
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Download failed.");
    }
  };

  const onDelete = async (node) => {
    if (!canDeleteComments || !node?.id || deleteBusyId) return;
    const ok = window.confirm(
      "Delete this comment? Replies and attachments under it will also be removed."
    );
    if (!ok) return;
    setDeleteBusyId(node.id);
    setError("");
    try {
      await deleteProjectComment(projectId, node.id);
      const removeIds = new Set([String(node.id)]);
      setFlat((prev) => {
        let changed = true;
        while (changed) {
          changed = false;
          for (const c of prev) {
            if (
              c.parentId != null &&
              removeIds.has(String(c.parentId)) &&
              !removeIds.has(String(c.id))
            ) {
              removeIds.add(String(c.id));
              changed = true;
            }
          }
        }
        return prev.filter((c) => !removeIds.has(String(c.id)));
      });
      if (replyParentId != null && removeIds.has(String(replyParentId))) {
        setReplyParentId(null);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not delete comment.");
    } finally {
      setDeleteBusyId(null);
    }
  };

  return (
    <section
      className={
        sectionClassName ||
        "project-detail-section project-detail-section-card project-comments-section"
      }
      aria-label="Project comments"
    >
      <h2 className="project-detail-section-heading">Comments</h2>
      <p className="project-detail-muted project-comments-intro">
        Discuss this project, attach files, and mention teammates with{" "}
        <span className="project-detail-kbd">@username</span> (known users only).
        {projectTitle ? ` · ${projectTitle}` : ""}
      </p>

      {loading ? <p className="project-detail-muted">Loading comments…</p> : null}
      {error ? (
        <p className="user-profile-settings-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="project-comments-thread">
        {tree.length === 0 && !loading ? (
          <p className="project-detail-muted">No comments yet. Start the thread below.</p>
        ) : null}
        {tree.map((node) => (
          <CommentBranch
            key={node.id}
            node={node}
            projectId={projectId}
            depth={0}
            onReply={(id) => setReplyParentId(id)}
            replyOpenId={replyParentId}
            onUploadFile={onUploadFile}
            onDownload={onDownload}
            onDelete={onDelete}
            canDelete={canDeleteComments}
            deleteBusyId={deleteBusyId}
            uploadBusyId={uploadBusyId}
          />
        ))}
      </div>

      <div className="project-comments-composer">
        {replyParentId ? (
          <div className="project-comments-reply-bar">
            <span>Replying to comment #{replyParentId}</span>
            <button type="button" className="project-comments-text-btn" onClick={() => setReplyParentId(null)}>
              Cancel
            </button>
          </div>
        ) : null}
        {viewerAuthorProfile ? (
          <div className="project-comments-composer-identity">
            <CommentAuthorDisplay
              email={viewerAuthorProfile.email}
              authorProfile={viewerAuthorProfile}
            />
          </div>
        ) : null}
        <CommentMentionTextarea
          rows={4}
          placeholder="Write a comment… Type @ to mention someone."
          value={composerBody}
          onChange={setComposerBody}
        />
        <div className="project-comments-composer-actions">
          <button
            type="button"
            className={submitButtonClassName}
            onClick={submitComment}
            disabled={submitting || !composerBody.trim()}
          >
            {submitting ? "Posting…" : replyParentId ? "Post reply" : "Post comment"}
          </button>
        </div>
      </div>
    </section>
  );
}
